// Spec-driven role lookups for workflow templates.
//
// Provider specs may declare `_meta.roles` per task, mapping semantic roles
// (aspect / resolution / duration / model / prompt) to the provider field
// key that fulfills them, e.g. `{ "aspect": "ratio", "model": "model_name" }`.
// Consumers read roles first and fall back to the hardcoded key-name lists
// below for templates that predate `_meta.roles`.

export const ROLE_FALLBACK_KEYS = {
  model: ['model', 'model_name', 'model_id'],
  aspect: ['aspect_ratio', 'ratio', 'aspectRatio'],
  resolution: ['resolution', 'image_size', 'imageSize', 'size'],
  duration: ['duration', 'total_duration'],
  prompt: ['promptText', 'prompt', 'positivePrompt', 'text'],
}

export function parseFlowTemplate(flow) {
  const raw = flow?.template
  if (!raw) return null
  if (typeof raw === 'object') return raw
  if (typeof raw !== 'string') return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function findLinkedTaskType(flow) {
  const inputs = flow?.inputs && typeof flow.inputs === 'object' ? flow.inputs : {}
  for (const input of Object.values(inputs)) {
    const links = input?.links && typeof input.links === 'object' ? input.links : null
    if (!links) continue
    const first = Object.values(links).find((link) => link?.node)
    if (first?.node) return first.node
  }
  const template = parseFlowTemplate(flow)
  const keys = template && typeof template === 'object' ? Object.keys(template) : []
  return keys[0] || ''
}

export function flowTaskMeta(flow) {
  const template = parseFlowTemplate(flow)
  const taskType = findLinkedTaskType(flow)
  const meta = taskType ? template?.[taskType]?._meta : null
  return meta && typeof meta === 'object' ? meta : null
}

export function taskRoles(template, taskKey) {
  const roles = taskKey ? template?.[taskKey]?._meta?.roles : null
  return roles && typeof roles === 'object' ? roles : null
}

// Provider field key declared for a role in the flow's linked task, or ''.
export function flowRoleKey(flow, role) {
  const meta = flowTaskMeta(flow)
  const key = meta?.roles && typeof meta.roles === 'object' ? meta.roles[role] : null
  return typeof key === 'string' && key.trim() ? key.trim() : ''
}

export function flowRoleSpecification(flow, role) {
  const meta = flowTaskMeta(flow)
  const specification =
    meta?.specification && typeof meta.specification === 'object'
      ? meta.specification
      : {}
  const declaredKey = flowRoleKey(flow, role)
  const fallbackKey = (ROLE_FALLBACK_KEYS[role] || []).find(
    (key) => specification[key],
  )
  const key = declaredKey || fallbackKey || ''
  return key && specification[key] && typeof specification[key] === 'object'
    ? specification[key]
    : null
}

function filterOptionsByModelCapability(meta, capabilityKey, options, model) {
  const list = Array.isArray(options) ? options : []
  const selectedModel = `${model || ''}`.trim()
  const byModel = meta?.capabilities?.[capabilityKey]
  if (!selectedModel || !byModel || typeof byModel !== 'object') return list
  const allowed = byModel[selectedModel]
  if (!Array.isArray(allowed)) return list
  return list.filter((option) => allowed.includes(option))
}

export function taskAspectOptionsForModel(meta, options, model) {
  return filterOptionsByModelCapability(meta, 'ratiosByModel', options, model)
}

export function flowAspectOptionsForModel(flow, options, model) {
  return taskAspectOptionsForModel(flowTaskMeta(flow), options, model)
}

export function taskResolutionOptionsForModel(meta, options, model) {
  return filterOptionsByModelCapability(meta, 'resolutionsByModel', options, model)
}

export function flowResolutionOptionsForModel(flow, options, model) {
  return taskResolutionOptionsForModel(flowTaskMeta(flow), options, model)
}

// Duration option values are stored as bare integer-second strings ("5").
// Runway's handler runs them through `Number()` + an integer check, so a
// "5s" style value fails the request outright; PiAPI tolerates it. Labels
// re-add the unit at render time instead.
const DURATION_UNIT_SUFFIX = /\s*(?:seconds|second|secs|sec|s)$/

export function normalizeDurationValue(value) {
  const raw = `${value ?? ''}`.trim().toLowerCase()
  if (!raw) return ''
  const num = Number(raw.replace(DURATION_UNIT_SUFFIX, '').trim())
  if (!Number.isFinite(num) || num <= 0) return ''
  return `${Math.round(num)}`
}

export function normalizeDurationList(list) {
  const out = []
  ;(Array.isArray(list) ? list : []).forEach((item) => {
    const value = normalizeDurationValue(item)
    if (value && !out.includes(value)) out.push(value)
  })
  return out
}

export function formatDurationLabel(value) {
  const normalized = normalizeDurationValue(value)
  return normalized ? `${normalized}s` : `${value ?? ''}`.trim()
}

// Every provider spec declares duration as `type: "number"`, so the enum has
// to be read regardless of declared type (Runway ships a usable [2..10]) and
// synthesized from min/max/step when absent (all PiAPI tasks carry only
// constraints). The cap keeps a malformed spec from producing a huge menu.
const MAX_GENERATED_DURATION_OPTIONS = 60

export function durationOptionsFromSpec(fieldSpec) {
  const fromEnum = normalizeDurationList(fieldSpec?.enum)
  if (fromEnum.length) return fromEnum

  const constraints =
    fieldSpec?.constraints && typeof fieldSpec.constraints === 'object'
      ? fieldSpec.constraints
      : {}
  const min = Number(constraints.min)
  const max = Number(constraints.max)
  if (!Number.isFinite(min) || !Number.isFinite(max) || min <= 0 || max < min) {
    return []
  }
  const rawStep = Number(constraints.step)
  const step = Number.isFinite(rawStep) && rawStep > 0 ? rawStep : 1

  const generated = []
  for (
    let value = min;
    value <= max + 1e-9 && generated.length < MAX_GENERATED_DURATION_OPTIONS;
    value += step
  ) {
    generated.push(value)
  }
  return normalizeDurationList(generated)
}

export function flowDurationMaxSeconds(flow, fallback = 15) {
  const durationSpec = flowRoleSpecification(flow, 'duration')
  const constrainedMax = Number(durationSpec?.constraints?.max)
  if (Number.isFinite(constrainedMax) && constrainedMax > 0) {
    return constrainedMax
  }

  const enumValues = Array.isArray(durationSpec?.enum)
    ? durationSpec.enum
        .map((value) => Number.parseFloat(value))
        .filter((value) => Number.isFinite(value))
    : []
  if (enumValues.length) return Math.max(...enumValues)

  const safeFallback = Number(fallback)
  return Number.isFinite(safeFallback) && safeFallback > 0 ? safeFallback : 15
}
